import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";
import { Button, Checkbox, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { TeachingPopoverBody, TeachingPopoverTitle, TeachingPopoverSurface } from "@fluentui/react-components";
import "@justinribeiro/lite-youtube";

const useStyles = makeStyles({
  teachingPopover: {
    margin: "auto",
    position: "fixed",
    width: "auto",
    maxWidth: "unset",
    left: "20px",
    right: "20px",
    bottom: "85px",
    padding: tokens.spacingVerticalXXL,
    "@media (min-width: 992px)": {
      left: "30px",
      width: "500px",
      maxWidth: "500px",
      right: "unset"
    },
    "&.mobile": {
      bottom: "250px"
    }
  },
  title: {
    marginBottom: tokens.spacingVerticalM,
    fontSize: tokens.fontSizeBase500
  },
  inlinePopoverFooter: {
    paddingTop: "8px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  buttonsContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  video: {
    margin: "-24px",
    marginBottom: "24px",
    opacity: 0,
    transition: "opacity 0.5s",
    background: "#000",
    borderRadius: tokens.borderRadiusXLarge,
    "& *": {
      borderRadius: tokens.borderRadiusXLarge
    },
    "&.visible": {
      opacity: 1
    }
  }
});

export function FluentTips({
  className,
  step,
  children,
  onDismiss,
  activeStep,
  dismissLabel,
  onSetTipSelected,
  buttonStart,
  activeButtonLabel,
  hideSkipButton,
  header,
  content,
  isMobile,
  dontShowAgainLabel = null,
  onDontShowAgainChange = null,
  restartButton = false,
  restartLabel = "Restart Tour",
  onRestartTour = null,
  videoId
}) {
  const intl = useIntl();
  const styles = useStyles();

  // Add a 100ms delay before adding the "visible" class
  const [videoVisible, setVideoVisible] = useState(false);
  useEffect(() => {
    if (!videoId) return;
    setVideoVisible(false);
    const timeout = setTimeout(() => setVideoVisible(true), 100);
    return () => clearTimeout(timeout);
  }, [videoId]);

  return (
    <TeachingPopoverSurface className={mergeClasses(styles.teachingPopover, isMobile ? "mobile" : "", className)}>
      <TeachingPopoverBody>
        {videoId && (
          <div className={mergeClasses(styles.video, videoVisible && "visible")}>
            <lite-youtube videoid={videoId} posterloading="eager" params="rel=0"></lite-youtube>
          </div>
        )}
        <TeachingPopoverTitle className={styles.title}>{header}</TeachingPopoverTitle>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "start", gap: "8px" }}>
          {content || children}
        </div>
      </TeachingPopoverBody>
      <div className={styles.inlinePopoverFooter}>
        <div className={styles.checkboxContainer}>
          {dontShowAgainLabel && onDontShowAgainChange && (
            <Checkbox
              onChange={onDontShowAgainChange}
              label={intl.formatMessage({
                id: dontShowAgainLabel,
                defaultMessage: dontShowAgainLabel.header
              })}
            />
          )}
        </div>
        <div className={styles.buttonsContainer}>
          {!hideSkipButton && (
            <Button appearance="secondary" onClick={onDismiss}>
              {dismissLabel}
            </Button>
          )}
          {restartButton && (
            <Button appearance="secondary" onClick={onRestartTour}>
              {restartLabel}
            </Button>
          )}
          {buttonStart && (
            <Button appearance="primary" onClick={onSetTipSelected}>
              {intl.formatMessage({
                id: activeButtonLabel?.id,
                defaultMessage: activeButtonLabel.header
              })}
            </Button>
          )}
        </div>
      </div>
    </TeachingPopoverSurface>
  );
}

FluentTips.propTypes = {
  className: PropTypes.string,
  step: PropTypes.array,
  onSetTipSelected: PropTypes.func,
  buttonStart: PropTypes.bool,
  hideSkipButton: PropTypes.bool,
  activeStep: PropTypes.number,
  activeButtonLabel: PropTypes.object,
  children: PropTypes.node,
  onDismiss: PropTypes.func,
  dismissLabel: PropTypes.node,
  isMobile: PropTypes.bool,
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  content: PropTypes.node,
  dontShowAgainLabel: PropTypes.object,
  onDontShowAgainChange: PropTypes.func,
  restartButton: PropTypes.bool,
  restartLabel: PropTypes.string,
  onRestartTour: PropTypes.func,
  videoId: PropTypes.string
};
